import { Injectable, inject } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import {
  BenefitPlanFundingSourceAndSchedule,
  BenefitPlanState,
  ElectionScheduleType,
  FundingPostingType,
  FundingSourceType,
} from '@models/configuration/model';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs';

import { BenefitPlanQuery } from '../benefit-plan/benefit-plan.query';
import { BenefitPlanFundingSourceAndScheduleState, BenefitPlanFundingSourceAndScheduleStore } from './benefit-plan-funding-source-and-schedule.store';
import { Dates } from '@app/shared/utils/dates';

@Injectable({
  providedIn: 'root',
})
export class BenefitPlanFundingSourceAndScheduleQuery extends QueryEntity<BenefitPlanFundingSourceAndScheduleState> {
  protected store: BenefitPlanFundingSourceAndScheduleStore;
  private benefitPlanQuery = inject(BenefitPlanQuery);

  constructor() {
    const store = inject(BenefitPlanFundingSourceAndScheduleStore);

    super(store);

    this.store = store;
  }

  public canViewPODReports(): Observable<boolean> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() =>
        this.selectAll({
          filterBy: (entity) => entity.fundingPosting === FundingPostingType.PointOfDisbursement,
        })
      ),
      switchMap((entities) => {
        const benefitPlanIds = entities.map((entity) => entity.parentId);
        return this.benefitPlanQuery.selectManyWhenLoaded(benefitPlanIds);
      }),
      map((entities) => entities.some((entity) => entity.currentState !== BenefitPlanState.Finalized))
    );
  }

  public selectAllWhenLoaded(): Observable<BenefitPlanFundingSourceAndSchedule[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() => this.selectAll())
    );
  }

  public selectByBenefitPlan(benefitPlanId: string): Observable<BenefitPlanFundingSourceAndSchedule[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() =>
        this.selectAll({
          filterBy: (entity) => entity.parentId === benefitPlanId,
        })
      )
    );
  }

  public selectByBenefitPlanForIndividualElection(benefitPlanId: string, effectiveDate: string): Observable<BenefitPlanFundingSourceAndSchedule[]> {
    return this.selectLoading().pipe(
      filter((isLoading) => !isLoading),
      switchMap(() =>
        this.selectAll({
          filterBy: (entity) =>
            entity.parentId === benefitPlanId &&
            entity.fundingSource === FundingSourceType.ParticipantToClient &&
            entity.electionScheduleType !== ElectionScheduleType.UserInitiated &&
            (entity.startDate && entity.endDate ? Dates.isBetween(effectiveDate, entity.startDate, entity.endDate) : true),
        })
      )
    );
  }
}
